import {
  useEffect,
  useId,
  useRef,
  type KeyboardEvent
} from "react";
import {
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  ListOrdered,
  Quote
} from "lucide-react";

import type { Difficulty } from "@/domain";
import type { ActivityHostRenderContext } from "@/features/activity-host";
import type { CurriculumAudioPlayer } from "@/features/audio";
import { IconButton, PrimaryCommandButton } from "@/ui";

import type {
  StorySequenceAction,
  StorySequenceRenderModel
} from "../adapters";
import {
  ChineseDifficultyBadge,
  ChineseFeedbackNotice
} from "./shared";

export interface StorySequenceRendererProps {
  readonly audioPlayer: CurriculumAudioPlayer;
  readonly context: ActivityHostRenderContext<
    StorySequenceAction,
    StorySequenceRenderModel
  >;
  readonly difficulty: Difficulty;
}

export function StorySequenceRenderer({
  audioPlayer,
  context,
  difficulty
}: StorySequenceRendererProps) {
  const headingId = useId();
  const itemRefs = useRef(new Map<string, HTMLLIElement>());
  const { dispatch, getActivationProps, model } = context;
  const firstItemId = model.items[0]?.id ?? null;

  useEffect(() => {
    if (model.focusedItemId !== null) {
      itemRefs.current.get(model.focusedItemId)?.focus();
    }
  }, [model.focusedItemId]);

  function handleItemKeyDown(
    event: KeyboardEvent<HTMLLIElement>,
    itemId: string
  ): void {
    if (event.currentTarget !== event.target) {
      return;
    }

    switch (event.key) {
      case "ArrowUp":
      case "ArrowLeft":
        event.preventDefault();
        dispatch(
          {
            type: "move-item",
            itemId,
            direction: "backward",
            inputMethod: "keyboard"
          },
          "keyboard"
        );
        break;
      case "ArrowDown":
      case "ArrowRight":
        event.preventDefault();
        dispatch(
          {
            type: "move-item",
            itemId,
            direction: "forward",
            inputMethod: "keyboard"
          },
          "keyboard"
        );
        break;
    }
  }

  return (
    <section
      aria-labelledby={headingId}
      className="chinese-player__activity chinese-player__sequence"
      data-difficulty={difficulty}
      data-reduced-motion={context.reducedMotion}
    >
      <header className="chinese-player__activity-heading">
        <span aria-hidden="true" className="chinese-player__activity-mark">
          <ListOrdered />
        </span>
        <div>
          <ChineseDifficultyBadge difficulty={difficulty} />
          <h2 id={headingId}>把故事排成正确顺序</h2>
        </div>
      </header>

      <blockquote className="chinese-player__narration">
        <Quote aria-hidden="true" />
        <p>{model.narration}</p>
      </blockquote>

      <ol
        aria-label="故事卡片顺序"
        className="chinese-player__sequence-list"
      >
        {model.items.map((item) => (
          <li
            aria-label={
              "第 " +
              String(item.position) +
              " 步，" +
              item.label
            }
            className="chinese-player__sequence-item"
            data-focused={item.focused}
            data-hinted={item.hinted}
            key={item.id}
            onFocus={() =>
              dispatch(
                {
                  type: "focus-item",
                  itemId: item.id,
                  inputMethod: "keyboard"
                },
                "keyboard"
              )
            }
            onKeyDown={(event) => handleItemKeyDown(event, item.id)}
            ref={(node) => {
              if (node === null) {
                itemRefs.current.delete(item.id);
              } else {
                itemRefs.current.set(item.id, node);
              }
            }}
            tabIndex={
              item.focused ||
              (model.focusedItemId === null && item.id === firstItemId)
                ? 0
                : -1
            }
          >
            <span
              aria-hidden="true"
              className="chinese-player__sequence-number"
            >
              {item.position}
            </span>
            <span className="chinese-player__sequence-label">
              {item.label}
            </span>
            <div
              aria-label={"移动“" + item.label + "”"}
              className="chinese-player__sequence-actions"
              role="group"
            >
              <IconButton
                {...getActivationProps((inputMethod) => ({
                  type: "move-item",
                  itemId: item.id,
                  direction: "backward",
                  inputMethod
                }))}
                disabled={!item.canMoveBackward}
                icon={ArrowUp}
                label={"把" + item.label + "向前移动"}
                tooltip="向前移动"
              />
              <IconButton
                {...getActivationProps((inputMethod) => ({
                  type: "move-item",
                  itemId: item.id,
                  direction: "forward",
                  inputMethod
                }))}
                disabled={!item.canMoveForward}
                icon={ArrowDown}
                label={"把" + item.label + "向后移动"}
                tooltip="向后移动"
              />
            </div>
          </li>
        ))}
      </ol>

      <div className="chinese-player__sequence-footer">
        <ChineseFeedbackNotice
          audioPlayer={audioPlayer}
          enabled={!context.isPaused}
          feedback={model.feedback}
        />
        <PrimaryCommandButton
          {...getActivationProps((inputMethod) => ({
            type: "check-order",
            inputMethod
          }))}
          icon={CheckCircle2}
        >
          检查顺序
        </PrimaryCommandButton>
      </div>
    </section>
  );
}
